const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Get auth token from localStorage
 */
function getToken() {
  return localStorage.getItem('token');
}

/**
 * Set auth token in localStorage
 */
export function setToken(token) {
  localStorage.setItem('token', token);
}

/**
 * Remove auth token from localStorage
 */
export function removeToken() {
  localStorage.removeItem('token');
}

/**
 * Make an API request
 */
async function apiRequest(endpoint, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'API request failed');
  }

  return data;
}

// Auth API
export const authAPI = {
  login: (email, password) =>
    apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  register: (email, password, name) =>
    apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    }),

  me: () => apiRequest('/auth/me'),

  logout: () => apiRequest('/auth/logout', { method: 'POST' }),
};

// Projects API
export const projectsAPI = {
  getAll: ({ page, limit, search } = {}) => {
    const params = new URLSearchParams();
    if (page) params.append('page', page);
    if (limit) params.append('limit', limit);
    if (search) params.append('search', search);
    return apiRequest(`/projects?${params.toString()}`);
  },

  getById: (id) => apiRequest(`/projects/${id}`),

  create: (data) =>
    apiRequest('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id, data) =>
    apiRequest(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id) =>
    apiRequest(`/projects/${id}`, {
      method: 'DELETE',
    }),
};

// Sites API
export const sitesAPI = {
  getAll: (projectId, { page, limit, search } = {}) => {
    const params = new URLSearchParams();
    params.append('project_id', projectId);
    if (page) params.append('page', page);
    if (limit) params.append('limit', limit);
    if (search) params.append('search', search);
    return apiRequest(`/sites?${params.toString()}`);
  },

  create: (data) =>
    apiRequest('/sites', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id, data) =>
    apiRequest(`/sites/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id) =>
    apiRequest(`/sites/${id}`, {
      method: 'DELETE',
    }),
};

// Cameras API
export const camerasAPI = {
  getAll: (siteId, { page, limit, search } = {}) => {
    const params = new URLSearchParams();
    if (siteId) params.append('site_id', siteId);
    if (page) params.append('page', page);
    if (limit) params.append('limit', limit);
    if (search) params.append('search', search);
    return apiRequest(`/cameras?${params.toString()}`);
  },

  getAllByProject: (projectId, { page, limit, search } = {}) => {
    const params = new URLSearchParams();
    if (projectId) params.append('project_id', projectId);
    if (page) params.append('page', page);
    if (limit) params.append('limit', limit);
    if (search) params.append('search', search);
    return apiRequest(`/cameras?${params.toString()}`);
  },

  getById: (id) => apiRequest(`/cameras/${id}`),

  create: (data) =>
    apiRequest('/cameras', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id, data) =>
    apiRequest(`/cameras/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id) =>
    apiRequest(`/cameras/${id}`, {
      method: 'DELETE',
    }),

  updateImage: async (id, file) => {
    const uploadResult = await imagesAPI.upload(file, null, id);
    return apiRequest(`/cameras/${id}`, {
      method: 'PUT',
      body: JSON.stringify({
        image_url: uploadResult.image.url,
      }),
    });
  },
};

// Zones API
export const zonesAPI = {
  getAll: (cameraId, { page, limit, search } = {}) => {
    const params = new URLSearchParams();
    params.append('camera_id', cameraId);
    if (page) params.append('page', page);
    if (limit) params.append('limit', limit);
    if (search) params.append('search', search);
    return apiRequest(`/zones?${params.toString()}`);
  },

  create: (data) =>
    apiRequest('/zones', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id, data) =>
    apiRequest(`/zones/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id) =>
    apiRequest(`/zones/${id}`, {
      method: 'DELETE',
    }),
};

// Images API
export const imagesAPI = {
  upload: async (file, projectId, cameraId) => {
    const formData = new FormData();
    formData.append('image', file);
    if (projectId) formData.append('project_id', projectId);
    if (cameraId) formData.append('camera_id', cameraId);

    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/images/upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
      // Do not set Content-Type header, let the browser set it with boundary
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Upload failed');
    }

    return data;
  },

  getAll: (projectId, { page, limit } = {}) => {
    const params = new URLSearchParams();
    params.append('project_id', projectId);
    if (page) params.append('page', page);
    if (limit) params.append('limit', limit);
    return apiRequest(`/images?${params.toString()}`);
  },

  delete: (id) =>
    apiRequest(`/images/${id}`, {
      method: 'DELETE',
    }),
};

// System API
export const systemAPI = {
  status: () => apiRequest('/system/status'),
  metrics: () => apiRequest('/system/metrics'),
};

// Admin API
export const adminAPI = {
  // User management
  getUsers: (page = 1, limit = 50) =>
    apiRequest(`/admin/users?page=${page}&limit=${limit}`),

  getUser: (id) => apiRequest(`/admin/users/${id}`),

  createUser: (data) =>
    apiRequest('/admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateUser: (id, data) =>
    apiRequest(`/admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  changeUserRole: (id, role) =>
    apiRequest(`/admin/users/${id}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    }),

  deleteUser: (id) =>
    apiRequest(`/admin/users/${id}`, {
      method: 'DELETE',
    }),

  // System stats
  getStats: () => apiRequest('/admin/stats'),
};
